import React from 'react';
import { ArrowRight, Plus } from 'lucide-react';

export const ChallengeAndSolution = () => {
  const challenges = [
    {
      title: 'Accelerated Aging',
      description: 'Cellular damage affects how clients look and feel',
      image: 'https://images.unsplash.com/photo-1620790647593-b3a6916c7d60?w=600&q=80'
    },
    {
      title: 'Chronic Inflammation',
      description: 'Physical discomfort impacts treatment participation',
      image: 'https://images.pexels.com/photos/897817/pexels-photo-897817.jpeg?auto=compress&w=600'
    },
    {
      title: 'Low Energy and Fatigue',
      description: 'Limited energy affects engagement in activities',
      image: 'https://images.unsplash.com/photo-1607512774067-c0adbb97a205?w=600&q=80'
    },
    {
      title: 'Mental Health Challenges',
      description: 'Brain health impacts mood and cognitive function',
      image: 'https://images.unsplash.com/photo-1633144588631-959c772e2ecd?w=600&q=80'
    },
    {
      title: 'Emotional Strain',
      description: 'Physical wellness supports emotional healing',
      image: 'https://images.unsplash.com/photo-1595178156906-2396ef837b0f?w=600&q=80'
    },
    {
      title: 'Long-Term Health',
      description: 'Setting clients up for lasting wellness',
      image: 'https://images.unsplash.com/photo-1758691463084-17ed846d4a50?w=600&q=80'
    }
  ];

  const solutions = [
    {
      title: 'Cellular Rejuvenation',
      description: 'Neutralizes free radicals, supporting natural vitality and renewed energy'
    },
    {
      title: 'Reduced Inflammation',
      description: 'Antioxidant properties help reduce discomfort and promote physical comfort'
    },
    {
      title: 'Sustained Energy',
      description: 'Enhanced hydration supports consistent energy throughout the day'
    },
    {
      title: 'Cognitive Clarity',
      description: 'Supports brain health for improved focus and emotional stability'
    },
    {
      title: 'Enhanced Recovery',
      description: 'Physical wellness creates foundation for deeper emotional healing'
    },
    {
      title: 'Preventive Wellness',
      description: 'Builds strong cellular foundation for lasting health after treatment'
    }
  ];

  return (
    <section style={{
      background: '#2C3440',
      padding: '4rem 1.5rem 8rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Subtle Mountain Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.1
      }} />
      
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '5rem', maxWidth: '900px', margin: '0 auto 5rem' }}>
          <h2 style={{ 
            fontFamily: "'Playfair Display', Georgia, serif",
            marginBottom: '1.5rem', 
            color: '#FFFFFF',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 700,
            fontStyle: 'italic'
          }}>
            Take Your Work to a Deeper Level<br />A Cellular Level
          </h2>
          <p style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '1rem', color: '#E0E0E0', fontSize: 'clamp(1.125rem, 2vw, 1.25rem)', lineHeight: 1.6 }}>
            Your facility is doing amazing work with therapy, counseling, and emotional support. CRN complements your program by addressing the physical, cellular foundation that supports everything you're already doing.
          </p>
          <p style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 600, color: '#7EC0D6', fontSize: 'clamp(1.25rem, 2vw, 1.5rem)', fontStyle: 'italic' }}>
            When clients feel better physically, they engage more deeply in your existing treatment protocols.
          </p>
        </div>

        {/* Side by Side Comparison */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          gap: '2rem',
          alignItems: 'start',
          marginBottom: '5rem'
        }} className="challenge-solution-grid">
          {/* Left Column - Physical Aspects */}
          <div>
            <div style={{
              background: 'rgba(92, 112, 128, 0.6)',
              padding: '1.5rem',
              borderRadius: '1rem',
              textAlign: 'center',
              marginBottom: '2rem',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", margin: 0, color: '#FFFFFF', fontSize: '1.5rem', fontStyle: 'italic' }}>Physical Aspects of Recovery</h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {challenges.map((challenge, index) => (
                <div key={index} style={{
                  background: 'rgba(67, 75, 84, 0.8)',
                  padding: '1.5rem',
                  borderRadius: '0.75rem',
                  borderLeft: '3px solid #7EC0D6'
                }}>
                  <h4 style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '0.5rem', fontSize: '1.125rem', color: '#FFFFFF', fontWeight: 600 }}>
                    {challenge.title}
                  </h4>
                  <p style={{ fontFamily: "'Open Sans', sans-serif", margin: 0, color: '#E0E0E0', fontSize: '0.95rem' }}>
                    {challenge.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Center - Plus Icon */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: '5rem'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #00BFFF 0%, #5C7080 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 8px 24px rgba(0, 191, 255, 0.3)'
            }}>
              <Plus size={40} strokeWidth={3} />
            </div>
            <div style={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              marginTop: '2rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#00BFFF',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontFamily: "'Open Sans', sans-serif"
            }}>
              CRN Adds Depth
            </div>
          </div>

          {/* Right Column - Hydrogen Water Benefits */}
          <div>
            <div style={{
              background: 'linear-gradient(135deg, #00BFFF 0%, #5C7080 100%)',
              padding: '1.5rem',
              borderRadius: '1rem',
              textAlign: 'center',
              marginBottom: '2rem',
              color: 'white'
            }}>
              <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", margin: 0, color: 'white', fontSize: '1.5rem', fontStyle: 'italic' }}>How Hydrogen Water Therapy Enhances Recovery</h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {solutions.map((solution, index) => (
                <div key={index} style={{
                  background: 'rgba(67, 75, 84, 0.8)',
                  padding: '1.5rem',
                  borderRadius: '0.75rem',
                  borderLeft: '3px solid #00BFFF'
                }}>
                  <h4 style={{ fontFamily: "'Open Sans', sans-serif", marginBottom: '0.5rem', fontSize: '1.125rem', color: '#00BFFF', fontWeight: 600 }}>
                    {solution.title}
                  </h4>
                  <p style={{ fontFamily: "'Open Sans', sans-serif", margin: 0, color: '#E0E0E0', fontSize: '0.95rem' }}>
                    {solution.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div style={{
          background: 'rgba(92, 112, 128, 0.4)',
          padding: '3rem',
          borderRadius: '1rem',
          textAlign: 'center',
          border: '1px solid rgba(0, 191, 255, 0.3)'
        }}>
          <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", marginBottom: '1rem', color: '#FFFFFF', fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', fontStyle: 'italic' }}>
            The Result: Enhanced Treatment Outcomes
          </h3>
          <p style={{ fontFamily: "'Open Sans', sans-serif", maxWidth: '700px', margin: '0 auto 2rem', color: '#E0E0E0', fontSize: '1.125rem', lineHeight: 1.6 }}>
            When you add cellular restoration to your program, you're not replacing anything you do—you're adding a powerful foundation that makes everything else work better.
          </p>
          <button 
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-primary"
            style={{ fontSize: '1.125rem', padding: '1.25rem 2.5rem' }}
          >
            See How CRN Fits Your Program
            <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
          </button>
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .challenge-solution-grid {
            grid-template-columns: 1fr !important;
            gap: 3rem !important;
          }
          .challenge-solution-grid > div:nth-child(2) {
            display: none !important;
          }
        }
      `}</style>
    </section>
  );
};
